import React, { useState } from 'react';
import { useTranslation } from '../../i18n/LanguageContext';
import { deviceHeader } from '../../utils/deviceId';

/**
 * DeviceLimitModal
 * ─────────────────
 * Shown when /auth/login responds 423 with { code: 'device_limit_reached', active_devices, device_limit }.
 *
 * Lets the user pick one of their active devices to sign out, then retries the login
 * via the onConfirm callback. The parent (UserLogin) wraps the retry to actually call
 * the /auth/login endpoint again with the same credentials.
 *
 * Props:
 *   - open: bool
 *   - devices: array of { device_id, device_label, device_type, ip, last_active }
 *   - deviceLimit: number (3 by default)
 *   - email + password: the credentials we'll resubmit
 *   - API: backend base url
 *   - onCancel(): user closes the modal
 *   - onConfirm(deviceId): called after the chosen device is successfully kicked, parent retries login
 *   - isDarkMode
 */
export default function DeviceLimitModal({
  open,
  devices = [],
  deviceLimit = 3,
  email,
  password,
  API,
  onCancel,
  onConfirm,
  isDarkMode = false,
}) {
  const { t } = useTranslation();
  const [pickedId, setPickedId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  if (!open) return null;

  const handleConfirm = async () => {
    if (!pickedId) return;
    setSubmitting(true);
    setError('');
    try {
      // We don't have a JWT yet (login hasn't succeeded), so we use a transient
      // endpoint that takes email+password+device_id_to_revoke. To avoid building
      // that, we call the regular login WITH the X-Device-Id of the kicked device
      // briefly — NO. Simpler: backend exposes a special "kick before retry" endpoint
      // OR we re-login with a slot freed.
      //
      // Cleanest path: ask the backend to do "kick + login" in one shot via a header
      // that tells the existing /auth/login endpoint "if at limit, kick this device first".
      const formDataToSend = new FormData();
      formDataToSend.append('email', email);
      formDataToSend.append('password', password);
      const headers = {
        ...deviceHeader(),
        'X-Kick-Device-Id': pickedId,
      };
      const response = await fetch(`${API}/auth/login`, {
        method: 'POST',
        body: formDataToSend,
        headers,
      });
      const data = await response.json();
      if (response.ok) {
        onConfirm(data);
      } else {
        setError(typeof data.detail === 'string' ? data.detail : (data.detail?.reason || 'Sign-in failed. Try again.'));
      }
    } catch (e) {
      setError('Network error. Try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const deviceIcon = (type) => {
    if (type === 'phone') return '📱';
    if (type === 'tablet') return '📱';
    return '💻';
  };

  const fmtTime = (iso) => {
    if (!iso) return '';
    try {
      const d = new Date(iso);
      const diffMin = Math.round((Date.now() - d.getTime()) / 60000);
      if (diffMin < 1) return t('just_now') || 'Just now';
      if (diffMin < 60) return `${diffMin} ${t('min_ago') || 'min ago'}`;
      const diffHr = Math.round(diffMin / 60);
      if (diffHr < 24) return `${diffHr} ${t('hours_ago') || 'h ago'}`;
      const diffDay = Math.round(diffHr / 24);
      return `${diffDay} ${t('days_ago') || 'd ago'}`;
    } catch {
      return '';
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className={`w-full max-w-md rounded-2xl shadow-2xl overflow-hidden ${isDarkMode ? 'bg-gray-900 ring-1 ring-gray-700' : 'bg-white ring-1 ring-gray-200'}`}>
        {/* Header */}
        <div className={`px-5 py-4 border-b ${isDarkMode ? 'border-gray-800 bg-gray-900' : 'border-gray-100 bg-gradient-to-r from-blue-50 to-purple-50'}`}>
          <h2 className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            {t('device_limit_title') || `Device limit reached (${deviceLimit})`}
          </h2>
          <p className={`text-sm mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            {t('device_limit_subtitle') || 'To sign in here, choose a device to sign out:'}
          </p>
        </div>

        {/* Body */}
        <div className="p-5 space-y-2 max-h-[60vh] overflow-y-auto">
          {devices.map((d) => (
            <label
              key={d.device_id}
              className={`flex items-center gap-3 p-3 rounded-xl cursor-pointer border transition-colors ${
                pickedId === d.device_id
                  ? (isDarkMode ? 'bg-blue-900/30 border-blue-500' : 'bg-blue-50 border-blue-400')
                  : (isDarkMode ? 'bg-gray-800 border-gray-700 hover:bg-gray-700/50' : 'bg-white border-gray-200 hover:bg-gray-50')
              }`}
            >
              <input
                type="radio"
                name="kick-device"
                value={d.device_id}
                checked={pickedId === d.device_id}
                onChange={() => setPickedId(d.device_id)}
                className="w-4 h-4 accent-blue-600"
              />
              <span className="text-2xl">{deviceIcon(d.device_type)}</span>
              <div className="flex-1 min-w-0">
                <div className={`text-sm font-semibold truncate ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  {d.device_label}
                </div>
                <div className={`text-xs truncate ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  {fmtTime(d.last_active)}
                </div>
              </div>
            </label>
          ))}
          {error && (
            <div className="px-3 py-2 rounded-lg bg-red-50 border border-red-200 text-red-600 text-sm">
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className={`px-5 py-4 border-t flex items-center justify-end gap-2 ${isDarkMode ? 'border-gray-800 bg-gray-900' : 'border-gray-100 bg-gray-50'}`}>
          <button
            type="button"
            onClick={onCancel}
            disabled={submitting}
            className={`px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50 ${isDarkMode ? 'text-gray-300 hover:bg-gray-800' : 'text-gray-700 hover:bg-gray-100'}`}
          >
            {t('cancel') || 'Cancel'}
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={!pickedId || submitting}
            className="px-4 py-2 rounded-lg text-sm font-semibold text-white bg-gradient-to-r from-blue-500 to-purple-600 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? (t('signing_in') || 'Signing in...') : (t('sign_out_and_continue') || 'Sign out & continue')}
          </button>
        </div>
      </div>
    </div>
  );
}
